import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const API_URL = "/api";

export interface EmailPrefs {
  emailNotifications: boolean;
}

export interface EmailLogRow {
  id: string;
  eventType: string;
  entityId: string | null;
  status: string;
  sentAt: string;
  errorMessage: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}`);
  const body = await res.json();
  return body.data as T;
}

export function useEmailPrefs() {
  return useQuery({
    queryKey: ["email-prefs"],
    queryFn: () => getJson<EmailPrefs>("/me/email-prefs"),
  });
}

export function useSetEmailPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: boolean) => {
      const res = await fetch(`${API_URL}/me/email-prefs`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ emailNotifications: value }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return (await res.json()).data as EmailPrefs;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-prefs"] });
      toast.success("Email preferences updated");
    },
    onError: () => toast.error("Failed to update preferences"),
  });
}

export function useEmailLogs() {
  return useQuery({
    queryKey: ["email-logs"],
    queryFn: () => getJson<EmailLogRow[]>("/me/email-logs"),
  });
}
